import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export default function ShippingArticle() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <article className="pt-24 md:pt-28 pb-16 md:pb-24">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-4xl mx-auto">
          <nav className="mb-6 text-sm text-foreground-500">
            <button onClick={() => navigate('/')} className="hover:text-primary-600 transition-colors">
              {t('nav.home')}
            </button>
            <span className="mx-2">/</span>
            <span className="text-foreground-700">Shipping Methods Compared</span>
          </nav>

          <header className="mb-10 md:mb-14">
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              AllChinaBuy Shipping Methods Compared: Costs, Speed & Tracking Guide
            </h1>
            <p className="text-sm text-foreground-500">
              Published: August 2026 · Updated: August 2026 · Reading time: 11 min
            </p>
          </header>

          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed space-y-6 md:space-y-8">
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Why Shipping Matters on AllChinaBuy
              </h2>
              <p>
                Shipping is one of the most important factors in the total cost of your AllChinaBuy purchase. A product that seems like a great deal can become expensive once shipping fees, customs duties, and potential delays are factored in. Understanding the different shipping methods available, their costs, delivery times, and tracking capabilities will help you make smarter buying decisions and avoid unpleasant surprises.
              </p>
              <p>
                AllChinaBuy offers several shipping options that cater to different budgets, timelines, and risk tolerances. Whether you are ordering a single item or consolidating a large haul, choosing the right shipping method can save you money and reduce stress. This guide breaks down each method so you can select the best option for your situation.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Shipping Comparison Table
              </h2>
              <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
                <table className="min-w-full text-sm border border-background-200/70 rounded-lg overflow-hidden">
                  <thead className="bg-background-100">
                    <tr>
                      <th className="text-left px-3 py-3 font-semibold text-foreground-900">Method</th>
                      <th className="text-left px-3 py-3 font-semibold text-foreground-900">Speed</th>
                      <th className="text-left px-3 py-3 font-semibold text-foreground-900">Cost</th>
                      <th className="text-left px-3 py-3 font-semibold text-foreground-900">Tracking</th>
                      <th className="text-left px-3 py-3 font-semibold text-foreground-900">Best For</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200/70">
                    <tr className="bg-background-50">
                      <td className="px-3 py-3 font-medium">EMS / ePacket</td>
                      <td className="px-3 py-3">10-20 days</td>
                      <td className="px-3 py-3">Medium</td>
                      <td className="px-3 py-3">Full</td>
                      <td className="px-3 py-3">Small to medium packages</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-3 font-medium">DHL Express</td>
                      <td className="px-3 py-3">3-7 days</td>
                      <td className="px-3 py-3">High</td>
                      <td className="px-3 py-3">Full</td>
                      <td className="px-3 py-3">Urgent, high-value items</td>
                    </tr>
                    <tr className="bg-background-50">
                      <td className="px-3 py-3 font-medium">UPS / FedEx</td>
                      <td className="px-3 py-3">5-10 days</td>
                      <td className="px-3 py-3">High</td>
                      <td className="px-3 py-3">Full</td>
                      <td className="px-3 py-3">Reliable commercial delivery</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-3 font-medium">Sea / Train Freight</td>
                      <td className="px-3 py-3">30-60 days</td>
                      <td className="px-3 py-3">Low</td>
                      <td className="px-3 py-3">Limited</td>
                      <td className="px-3 py-3">Very large, heavy hauls</td>
                    </tr>
                    <tr className="bg-background-50">
                      <td className="px-3 py-3 font-medium">Postal Small Packet</td>
                      <td className="px-3 py-3">15-30 days</td>
                      <td className="px-3 py-3">Low</td>
                      <td className="px-3 py-3">Basic</td>
                      <td className="px-3 py-3">Light, low-value items</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-foreground-500 mt-2">
                *Costs and delivery times are approximate and vary by destination country, package weight, and current shipping conditions. Always verify current rates at checkout.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Budget Shipping Options
              </h2>
              <p>
                If you are trying to keep costs low, budget shipping options like postal small packets or economy line services are the most affordable choices. These methods typically cost a fraction of express services but come with trade-offs: longer delivery times, basic or no tracking, and less reliable delivery. For small, lightweight items that are not time-sensitive, budget shipping can be a great way to save money.
              </p>
              <p>
                However, budget shipping has higher risks. Packages without tracking are more likely to be lost or delayed, and there is little recourse if something goes wrong. If the item value is low, this risk may be acceptable. For anything over $50 or items you need by a specific date, spending extra on tracked shipping is usually worth the peace of mind.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Express and Courier Services
              </h2>
              <p>
                Express courier services like DHL, UPS, and FedEx offer the fastest delivery times, usually between 3 and 10 days depending on your location. They provide full tracking, reliable delivery updates, and generally better customer service if issues arise. The downside is the cost, which can be two to three times higher than standard postal services, especially for heavier packages.
              </p>
              <p>
                Express shipping is ideal for high-value purchases, time-sensitive orders, or when you need the security of a well-tracked delivery. Many buyers use express shipping for their first few orders to test the waters, then switch to cheaper options once they are comfortable with the process. Consider splitting your haul into multiple shipments if you have a mix of urgent and non-urgent items.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Consolidation and Volume Shipping
              </h2>
              <p>
                One of the biggest advantages of using a warehouse-based service like AllChinaBuy is the ability to consolidate multiple orders into a single shipment. Instead of paying shipping for each individual item from different sellers, you can have everything sent to the warehouse, wait until all items arrive, and then ship them together. This dramatically reduces per-item shipping costs.
              </p>
              <p>
                For very large orders, sea or train freight becomes an economical option. These methods take much longer, often 30 to 60 days, but the cost per kilogram is significantly lower than air shipping. Freight shipping is best for bulk orders, heavy items, or when you are not in a hurry. Be aware that freight shipments may have limited tracking and require you to handle customs clearance yourself in some cases.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Customs and Import Duties
              </h2>
              <p>
                An often overlooked part of shipping costs is customs duties and import taxes. When your package crosses international borders, your local customs authority may charge taxes, duties, or handling fees. These costs depend on the value of your shipment, the type of goods, and the import regulations of your country. Some countries have a de minimis threshold below which no duties are charged, while others tax every incoming package.
              </p>
              <p>
                Express couriers often handle customs clearance for you and may bill you separately for any duties paid on your behalf. Postal services usually require you to pay duties at the post office or through an online portal before delivery. Research your country's import rules before placing large orders to avoid unexpected costs. Some buyers intentionally split large hauls into smaller packages to stay below duty thresholds.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Tracking Your Shipment
              </h2>
              <p>
                Once your package is shipped, you will receive a tracking number. Use this number to monitor the shipment's progress through the carrier's website or a universal tracking service. Pay attention to the key tracking milestones: package accepted, departure from origin country, arrival in destination country, customs clearance, and out for delivery. If a package is stuck at one stage for an unusually long time, it may be worth contacting the carrier or the warehouse for an update.
              </p>
              <p>
                Some tracking systems are more reliable than others. Express couriers generally provide the most detailed and frequently updated tracking. Postal services may only show major milestones, and budget shipping with no tracking leaves you completely in the dark until the package arrives. For valuable shipments, choose a method with tracking so you can follow the journey and address problems quickly.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Tips for Reducing Shipping Costs
              </h2>
              <div className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/70">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0" /><span>Consolidate multiple orders into a single shipment</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0" /><span>Remove unnecessary packaging like shoe boxes and extra tags to reduce weight</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0" /><span>Choose slower shipping for non-urgent items</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0" /><span>Split large hauls into smaller packages under duty thresholds</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0" /><span>Research your country's import duty rules before ordering</span></li>
                  <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0" /><span>Use shipping calculators to estimate costs before buying</span></li>
                </ul>
              </div>
            </section>

            <div className="bg-primary-50 rounded-lg p-5 md:p-6 border border-primary-200/70 mt-10">
              <p className="text-sm text-foreground-700 mb-4">
                <strong>Disclaimer:</strong> This website is an independent informational resource. Information can change and should be independently verified before making purchasing decisions. We are not affiliated with AllChinaBuy or any sellers mentioned. Always verify current shipping rates, delivery times, and policies directly on the platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 font-medium text-sm rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap">
                  Explore AllChinaBuy Spreadsheet
                  <i className="ri-arrow-right-line" />
                </a>
                <button onClick={() => navigate('/')} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-background-50 text-foreground-700 font-medium text-sm rounded-md border border-background-200 hover:bg-background-100 transition-colors whitespace-nowrap">
                  <i className="ri-home-line" />
                  Back to Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}